"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useState } from "react";
import LogoHorizontalColorLight from "@/assets/logo-horizontal-color-light.svg";
import { LanguageSwitcher } from "@/components/ui/language-switcher";

interface BlogNavigationProps {
  locale: string;
}

export function BlogNavigation({ locale }: BlogNavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const t = useTranslations("blog.navigation");

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-50/95 backdrop-blur-sm border-b border-gray-200 px-4">
      <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href={`/${locale}`} aria-label="Back to home">
            <LogoHorizontalColorLight className="h-10 md:h-14 text-black" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8 text-m tracking-wide">
            <Link
              href={`/${locale}`}
              className="hover:text-gray-600 font-medium transition-colors whitespace-nowrap"
            >
              {t("home")}
            </Link>

            <Link
              href={`/${locale}/blog`}
              className="hover:text-gray-600 font-bold transition-colors whitespace-nowrap text-brand-blue"
            >
              {t("blog")}
            </Link>

            <div className="flex items-center gap-4">
              <div className="text-gray-300 leading-none">|</div>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile Language Switcher - shown when menu is open */}
          {isMenuOpen && (
            <div className="md:hidden z-100 absolute left-4 animate-fade-in">
              <LanguageSwitcher />
            </div>
          )}

          {/* Mobile Hamburger Button */}
          <button
            type="button"
            onClick={toggleMenu}
            className="md:hidden flex flex-col justify-center items-center w-11 h-11 space-y-1.5 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2 relative z-100 min-w-[44px] min-h-[44px]"
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
          >
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "rotate-45 translate-y-2" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "opacity-0" : ""
              }`}
            />
            <span
              className={`block w-6 h-0.5 bg-gray-800 transition-all duration-300 ${
                isMenuOpen ? "-rotate-45 -translate-y-2" : ""
              }`}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`md:hidden fixed top-0 left-0 right-0 bg-gray-50/98 backdrop-blur-sm border-b border-gray-200 transition-all duration-300 ease-in-out z-40 pt-7 ${
          isMenuOpen
            ? "transform translate-y-0 opacity-100"
            : "transform -translate-y-full opacity-0"
        }`}
      >
        <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col space-y-5 text-lg tracking-wide">
            <Link
              href={`/${locale}`}
              onClick={closeMenu}
              className="hover:text-gray-600 font-medium transition-colors py-2"
            >
              {t("home")}
            </Link>

            <Link
              href={`/${locale}/blog`}
              onClick={closeMenu}
              className="hover:text-gray-600 font-bold text-brand-blue transition-colors py-2"
            >
              {t("blog")}
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
